import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Indicator,
  Input,
  Popover,
  PopoverButton,
  PopoverItem,
  SkeletonLine,
  StyledPopoverPanel,
  Tooltip,
} from '@crazyegginc/hatch';
import classNames from 'classnames';

import { ReactComponent as InfoIcon } from '@crazyegginc/hatch/dist/images/icon-info-circle-outline.svg';
import { ReactComponent as CeBalloonIcon } from '@crazyegginc/hatch/dist/images/logo-ce-balloon.svg';
import { ReactComponent as GoogleAnalyticsIcon } from '@crazyegginc/hatch/dist/images/logo-google-analytics.svg';
import { ReactComponent as TickIcon } from '@crazyegginc/hatch/dist/images/icon-tick-basic.svg';

import { Link, useMatches } from 'react-router-dom';
import { Pill } from '../Pill';
import { usePageParams } from '/src/hooks/usePageParams';
import { useAuthContext, useHasFeatureFlag, useModal, useSite } from '/src/hooks';
import { AddSnapshotNewSiteModal } from '/src/features/snapshots/components/dashboard/modals/AddSnapshotNewSiteModal';
import { ConfirmRedirectModal } from '/src/features/google-analytics/modals/ConfirmRedirect';
import { ManageGaPropertiesModal } from '/src/features/google-analytics/modals/ManageGaProperties';
import { ALL, ALL_SITES } from '/src/features/_global/constants';
import { defaultRouteConfig } from '/src/Router';

const FILTER_THRESHOLD = 8;

export const SOURCE_TYPE = {
  SITE: 'SITE',
  GA_PROPERTY: 'GA_PROPERTY',
};

export function useSelectedSource({ allowAll = false, allowedSites = null } = {}) {
  const { allowAllSites, enableGoogleAnalytics } = usePageConfig();
  const { selectSite } = useSite({ enableAllSites: allowAll || allowAllSites, dontSelectSite: true });
  const { currentAccount, isSharing, sharedResource } = useAuthContext();

  const sites = useMemo(
    () =>
      isSharing ? [{ id: sharedResource?.siteId, name: sharedResource?.siteName }] : (currentAccount?.sites ?? []),
    [isSharing, currentAccount?.sites, sharedResource?.siteId, sharedResource?.siteName],
  );
  const gaProperties = useMemo(
    () => (isSharing ? [] : (currentAccount?.gaProperties ?? [])),
    [isSharing, currentAccount?.gaProperties],
  );

  const allSources = useMemo(
    () => [
      ...sites.map((s) => ({ ...s, type: SOURCE_TYPE.SITE })),
      ...gaProperties.map((g) => ({ ...g, type: SOURCE_TYPE.GA_PROPERTY })),
    ],
    [sites, gaProperties],
  );

  const allUserSelectableSources = useMemo(() => {
    return allSources.filter((s) => {
      if (!enableGoogleAnalytics) {
        return s.type !== SOURCE_TYPE.GA_PROPERTY;
      }
      return true;
    });
  }, [allSources, enableGoogleAnalytics]);

  const allAutoSelectableSources = useMemo(() => {
    return allSources
      .filter((s) => {
        if (s.type === SOURCE_TYPE.SITE) {
          return s.active;
        }
        // for now always return GA properties
        return true;
      })
      .filter((s) => {
        if (!enableGoogleAnalytics) {
          return s.type !== SOURCE_TYPE.GA_PROPERTY;
        }
        return true;
      });
  }, [allSources, enableGoogleAnalytics]);

  const enabledSites = useMemo(() => {
    const sitesList = allowedSites ?? sites;
    return sitesList.map((s) => s.name);
  }, [allowedSites, sites]);

  const pageParamsConfig = useMemo(
    () => [
      {
        key: 'property',
        defaultValue: () => {
          if (enableGoogleAnalytics && localStorage.getItem('shell:property')) {
            return localStorage.getItem('shell:property');
          }
          return null;
        },
      },
      {
        key: 'site',
        defaultValue: () => {
          if (localStorage.getItem('shell:allsites') === 'true') {
            return ALL;
          }
          if (localStorage.getItem('shell:site') && enabledSites.includes(localStorage.getItem('shell:site'))) {
            return localStorage.getItem('shell:site');
          }
          if (allowAll || allowAllSites) {
            return ALL;
          }
          return null;
        },
      },
    ],
    [allowAll, allowAllSites, enabledSites, enableGoogleAnalytics],
  );

  const { status, params, setParams } = usePageParams(pageParamsConfig);

  const selectedSource = useMemo(() => {
    if (params?.site === 'ALL') {
      return sites;
    }
    if (allUserSelectableSources.length === 1) return allUserSelectableSources[0];
    if (!params.property && !params.site) return null;

    const targetSource = allUserSelectableSources.find((s) => {
      if (params.property) {
        return String(s.id) === String(params.property) && s.type === SOURCE_TYPE.GA_PROPERTY;
      }
      return s.name.toLowerCase() === params.site.toLowerCase() && s.type === SOURCE_TYPE.SITE;
    });

    return targetSource || allUserSelectableSources[0];
  }, [allUserSelectableSources, sites, params.property, params.site]);

  const setSelectedSource = useCallback(
    (source) => {
      if (source === ALL_SITES) {
        localStorage.removeItem('shell:site');
        localStorage.removeItem('shell:property');
        localStorage.setItem('shell:allsites', 'true');
        selectSite('ALL');
        setParams({
          site: 'ALL',
          property: null,
        });
        return;
      }

      if (source?.type === SOURCE_TYPE.GA_PROPERTY) {
        localStorage.removeItem('shell:allsites');
        localStorage.setItem('shell:property', source.id);
        setParams({
          site: null,
          property: source.id,
        });
      } else {
        localStorage.removeItem('shell:allsites');
        localStorage.removeItem('shell:property');
        localStorage.setItem('shell:site', source.name);
        selectSite(source);
        setParams({
          property: null,
          site: source.name,
        });
      }
    },
    [setParams, selectSite],
  );

  const resetSourceSelection = useCallback(() => {
    localStorage.removeItem('shell:allsites');
    localStorage.removeItem('shell:property');
    localStorage.removeItem('shell:site');
    setSelectedSource(allAutoSelectableSources[0]);
  }, [setSelectedSource, allAutoSelectableSources]);

  useEffect(() => {
    if (selectedSource) return;
    if (!allAutoSelectableSources.length) return;

    if (!params.property && !params.site) {
      setSelectedSource(allAutoSelectableSources[0]);
    }
  }, [selectedSource, allAutoSelectableSources, params.property, params.site, setSelectedSource]);

  return {
    status,
    selectedSource,
    sourceId: params.source_id,
    sourceType: params.source_type?.toUpperCase() ?? null,
    setSelectedSource,
    resetSourceSelection,
  };
}

export function usePageConfig() {
  const matches = useMatches();

  // Take the last (deepest) match that has a pageConfig
  const routeConfig =
    matches
      .map((m) => m.handle?.pageConfig)
      .filter(Boolean)
      .at(-1) ?? {};

  return { ...defaultRouteConfig, ...routeConfig };
}

function SelectorSection({ children }) {
  return <section className="px-2">{children}</section>;
}

function SelectorHeading({ Icon, iconClass, title, info }) {
  return (
    <div className={classNames('w-[384px] h-10 px-4 py-2 flex items-center space-x-2 select-none')}>
      <Icon className={classNames('w-5 h-5 p-0.5', iconClass)} />
      <span className="text-header-4">{title}</span>
      {info ? (
        <Tooltip tooltipContent={info} placement="top">
          <InfoIcon className="fill-current text-cadet-blue-500 w-4 h-4" />
        </Tooltip>
      ) : null}
    </div>
  );
}

function DashedSeparator() {
  return <span className="w-[354px] self-center my-2 border-t border-mystic-500 border-dashed" />;
}

function SelectorItemList({ children }) {
  return <div className="flex flex-col">{children}</div>;
}

function SelectorItem({ item, disabled = false, close, pill, title, subTitle }) {
  const { selectedSource, setSelectedSource } = useSelectedSource();

  const isSelected = selectedSource?.id === item.id;

  return (
    <PopoverItem
      className={classNames('w-[384px] pl-12 pr-4 py-2 flex items-center bg-white relative justify-between', {
        'hover:bg-solitude-500 cursor-pointer': !disabled,
        'hover:bg-transparent cursor-auto': disabled,
      })}
      onClick={() => {
        if (disabled) {
          close?.();
          return;
        }
        setSelectedSource(item);
        close?.();
      }}
      aria-label={title || item.name}
    >
      <div className="flex w-full items-center">
        <div className="flex flex-col w-full">
          <div className="flex w-full justify-between items-center">
            <div className="flex items-center">
              {isSelected ? (
                <TickIcon className="fill-current text-dodger-blue-500 absolute w-4 h-4 left-4 top-1/2 -translate-y-1/2" />
              ) : null}
              <span className={classNames('text-body-2', disabled && 'text-lynch-500')}>{title || item.name}</span>
            </div>
          </div>
          {subTitle ? <span className="text-body-4 mt-1">{subTitle}</span> : null}
        </div>
        <div className="flex-shrink-0">{pill}</div>
      </div>
    </PopoverItem>
  );
}

function SelectorGroup({ index, children }) {
  return (
    <>
      {index > 0 ? <DashedSeparator /> : null}
      <div className="flex flex-col">{children}</div>
    </>
  );
}

export function PickerActionLink({ to, disabled = false, children }) {
  return (
    <Link
      to={to}
      className={classNames(
        'w-full h-14 flex items-center text-dodger-blue-500 font-semibold text-sm border-t border-mystic-500 pl-6 pr-4',
        {
          'hover:bg-solitude-500': !disabled,
        },
      )}
    >
      {children}
    </Link>
  );
}

export function PickerActionButton({ onClick, disabled = false, children }) {
  return (
    <button
      onClick={onClick}
      className={classNames(
        'w-full h-14 flex items-center text-dodger-blue-500 font-semibold text-sm border-t border-mystic-500 pl-6 pr-4',
        {
          'hover:bg-solitude-500': !disabled,
        },
      )}
    >
      {children}
    </button>
  );
}

/* Common action buttons */

export function ManageSitesButton() {
  return <PickerActionLink to="/options/site-settings">Manage Sites</PickerActionLink>;
}

export function EnableOtherSitesButton() {
  return <PickerActionLink to="/options/site-settings">Enable Other Sites</PickerActionLink>;
}

export function ViewAllSitesButton({ onClick }) {
  return <PickerActionButton onClick={onClick}>View All Sites</PickerActionButton>;
}

function AddSnapshotsForAnotherSiteButton() {
  const modal = useModal();

  return (
    <PickerActionButton
      onClick={() => {
        modal.show(<AddSnapshotNewSiteModal />);
      }}
    >
      Add Snapshots for Another Site
    </PickerActionButton>
  );
}

function ConfigureGaAccounts({ close }) {
  const modal = useModal();

  return (
    <PickerActionButton
      onClick={() => {
        modal.show(<ManageGaPropertiesModal />);
        close?.();
      }}
    >
      Google Analytics Settings
    </PickerActionButton>
  );
}

function ConnectGaButton({ close }) {
  const modal = useModal();

  return (
    <PickerActionButton
      onClick={() => {
        modal.show(<ConfirmRedirectModal />);
        close?.();
      }}
    >
      Connect Google Analytics
    </PickerActionButton>
  );
}

/*
 * Lets a user select a site (if any) or a GA property (if any)
 */
export function SourceSelector({
  sites = [],
  gaProperties = [],
  disabled = false,
  loading = false,
  enableGoogleAnalytics = false,
  enableAllSites = false,
  showEnableOtherSites = false,
  addSnapshotOption = false,
  addSiteOption = false,
}) {
  const hasGaImport = useHasFeatureFlag('ga-import');
  const { selectedSource, setSelectedSource, resetSourceSelection } = useSelectedSource({
    allowAll: enableAllSites,
    allowedSites: sites,
  });
  const [search, setSearch] = useState('');
  const allSources = useMemo(() => [...sites, ...gaProperties], [sites, gaProperties]);
  const hasMoreThanOne = allSources.length > 1;
  const hasActions =
    enableGoogleAnalytics || enableAllSites || showEnableOtherSites || addSnapshotOption || addSiteOption;

  const searchable = sites.length + gaProperties.length > FILTER_THRESHOLD;
  const placeholderText =
    gaProperties.length === 0
      ? 'a site'
      : sites.length === 0
        ? 'a Google Analytics property'
        : 'a site or a Google Analytics property';

  useEffect(() => {
    if (selectedSource?.type === SOURCE_TYPE.GA_PROPERTY && !enableGoogleAnalytics) {
      resetSourceSelection();
    }
  }, [selectedSource, enableGoogleAnalytics, resetSourceSelection]);

  const filteredSources = useMemo(() => {
    const term = search.toLowerCase();

    return {
      // group sites by status and order by name
      sites: sites
        .filter((s) => s.name.toLowerCase().includes(term))
        .sort((a, b) => {
          return Number(b.active) - Number(a.active) || a.name.localeCompare(b.name);
        }),
      gaProperties: gaProperties.filter((p) => {
        return !!p.enabledAt && p.name.toLowerCase().includes(term);
      }),
    };
  }, [search, sites, gaProperties]);

  const groupedProperties = useMemo(() => {
    return Object.groupBy(filteredSources.gaProperties, ({ accountId }) => accountId);
  }, [filteredSources.gaProperties]);

  const displayText =
    Array.isArray(selectedSource) && selectedSource.length > 1
      ? 'All sites'
      : selectedSource
        ? selectedSource.name
        : null;

  return (
    <Popover>
      <>
        <PopoverButton
          disabled={!selectedSource || disabled || (!hasMoreThanOne && !hasActions)}
          className="flex m-0 items-center text-header-2 select-none text-white focus-visible:outline-white space-x-2"
        >
          {!loading && displayText ? <span>{displayText}</span> : <SkeletonLine className="w-[250px] bg-white" />}
          {selectedSource && !disabled && (hasMoreThanOne || hasActions) ? <Indicator type="dropdown" /> : null}
        </PopoverButton>

        <StyledPopoverPanel
          data-testid="sitepicker-dropdown"
          className={classNames('max-h-[592px] bg-white !p-0 flex flex-col overscroll-contain')}
        >
          {({ close }) => (
            <>
              <div
                className={classNames(
                  'flex-1 overflow-y-auto overflow-x-hidden overscroll-contain',
                  'scrollbar scrollbar-track-white-lilac-500 scrollbar-thumb-cadet-blue-500 scrollbar-thumb-rounded',
                )}
              >
                <div className="border-r flex flex-col border-mystic-500 w-[400px] py-2">
                  {searchable ? (
                    <div className="px-2 pb-2">
                      <Input
                        data-testid="SourceFilterInput"
                        placeholder={`Search for ${placeholderText}`}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                  ) : null}
                  {sites.length > 0 ? (
                    <SelectorSection>
                      <SelectorHeading Icon={CeBalloonIcon} iconClass="fill-current text-lima-500" title="All Sites" />
                      <SelectorItemList>
                        {filteredSources.sites.map((s) => (
                          <SelectorItem
                            key={`site_item_${s.id}`}
                            item={s}
                            pill={
                              s.active ? (
                                <Pill color="#0086E6" filled={true} className="h-6 font-semibold !text-white">
                                  Active
                                </Pill>
                              ) : (
                                <Pill color="#E1E6EF" filled={true} className="h-6 font-semibold">
                                  Inactive
                                </Pill>
                              )
                            }
                            close={close}
                          />
                        ))}
                        {filteredSources.sites.length === 0 ? (
                          <SelectorItem item={{ name: 'No sites match your criteria.' }} disabled={true} />
                        ) : null}
                      </SelectorItemList>
                    </SelectorSection>
                  ) : null}
                  {sites.length > 0 && gaProperties.length > 0 ? (
                    <span className="h-px bg-mystic-500 w-[354px] self-center my-2" />
                  ) : null}
                  {gaProperties.length > 0 ? (
                    <SelectorSection>
                      <SelectorHeading
                        Icon={GoogleAnalyticsIcon}
                        title="Google Analytics Properties"
                        info={
                          <span className="text-left">
                            Data is being pulled from your
                            <br />
                            connected Google Analytics
                            <br />
                            account.
                          </span>
                        }
                      />
                      <SelectorItemList>
                        {Object.entries(groupedProperties).map(([accountId, properties], idx) => (
                          <SelectorGroup index={idx} key={`ga_${accountId}`}>
                            {properties.map((g) => (
                              <SelectorItem
                                key={`ga_item_${g.id}`}
                                item={g}
                                close={close}
                                subTitle={properties[0]?.accountName}
                                pill={
                                  g.lastError ? (
                                    <Tooltip
                                      tooltipContent={
                                        <div className="flex flex-col px-4 py-2 w-[240px] text-left leading-5 text-sm font-normal space-y-2.5">
                                          <span className="w-[215px]">
                                            We ran into a problem syncing data from Google Analytics into Crazy Egg.
                                          </span>
                                          <span className="w-[215px]">
                                            We’re on it and will update your Web Analytics dashboard as soon as data
                                            comes in. If the problem persists, contact Support for help.
                                          </span>
                                        </div>
                                      }
                                      placement="top"
                                    >
                                      <Pill color="#EF9906" filled={true} className="h-6 font-semibold !text-white">
                                        Error
                                      </Pill>
                                    </Tooltip>
                                  ) : null
                                }
                              />
                            ))}
                          </SelectorGroup>
                        ))}
                        {Object.keys(groupedProperties).length === 0 ? (
                          <SelectorItem
                            item={{ name: 'No properties match your criteria.' }}
                            disabled={true}
                            onClick={null}
                          />
                        ) : null}
                      </SelectorItemList>
                    </SelectorSection>
                  ) : null}
                </div>
              </div>
              <div className="flex flex-col">
                {enableAllSites ? (
                  <ViewAllSitesButton
                    onClick={() => {
                      setSelectedSource(ALL_SITES);
                      close?.();
                    }}
                  />
                ) : null}
                {hasGaImport && enableGoogleAnalytics ? (
                  <>
                    {gaProperties.length > 0 ? (
                      <ConfigureGaAccounts close={close} />
                    ) : (
                      <ConnectGaButton close={close} />
                    )}
                  </>
                ) : null}
                {addSnapshotOption ? <AddSnapshotsForAnotherSiteButton /> : null}
                {showEnableOtherSites ? <EnableOtherSitesButton /> : null}
                {addSiteOption ? <ManageSitesButton /> : null}
              </div>
            </>
          )}
        </StyledPopoverPanel>
      </>
    </Popover>
  );
}
